import React, { useState, useEffect, forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Box, 
  IconButton, 
  Paper, 
  TextField, 
  Button, 
  Snackbar, 
  Alert, 
  Tabs, 
  Tab,
  Dialog,
  DialogContent,
  Grid,
  List,
  ListItem,
  ListItemText,
  Chip,
  AppBar,
  Toolbar,
  Slide,
  CircularProgress
} from '@mui/material';
import { ArrowBack, Save, ListAlt, DeleteSweep, Close, Sync, CloudDownload, Assignment, SmartToy, RestartAlt, Translate, Category } from '@mui/icons-material';
import Loader from './Loader';
import { db } from './Dexie';
import { useLiveQuery } from 'dexie-react-hooks';
import { syncProducts, syncAttributes, syncCategories, syncProductAttributes } from '../services/productService';
import { log } from '../services/loggingService';
import { getTelegramTranslations } from '../services/translationService';

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function Settings() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [settings, setSettings] = useState({
    siteName: '',
    apiEndpoint: '',
  });
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isResettingTelegram, setIsResettingTelegram] = useState(false);
  const [isResettingTranslations, setIsResettingTranslations] = useState(false);

  // Ottieni i log in tempo reale
  const logs = useLiveQuery(() => db.logs.reverse().toArray());

  useEffect(() => {
    const loadSettings = async () => {
      const siteName = await db.genericTable.get({ table: 'settings', field: 'siteName' });
      const apiEndpoint = await db.genericTable.get({ table: 'settings', field: 'apiEndpoint' });
      
      setSettings({
        siteName: siteName ? siteName.value : '',
        apiEndpoint: apiEndpoint ? apiEndpoint.value : '',
      });
    };
    loadSettings();
  }, []);

  const handleSave = async () => {
    try {
      await db.genericTable.put({ table: 'settings', field: 'siteName', value: settings.siteName });
      await db.genericTable.put({ table: 'settings', field: 'apiEndpoint', value: settings.apiEndpoint });
      
      // Aggiungi un log del salvataggio
      await db.logs.add({
        timestamp: new Date().toISOString(),
        type: 'INFO',
        message: 'Impostazioni aggiornate',
        details: `SiteName: ${settings.siteName}, API: ${settings.apiEndpoint}`
      });

      setToast({ open: true, message: 'Impostazioni salvate con successo!', severity: 'success' });
    } catch (error) {
      setToast({ open: true, message: 'Errore durante il salvataggio.', severity: 'error' });
    }
  };

  const clearLogs = async () => {
    if (window.confirm('Sei sicuro di voler eliminare tutti i log?')) {
      await db.logs.clear();
      setToast({ open: true, message: 'Log eliminati.', severity: 'info' });
    }
  };

  const handleSync = async (force = false) => {
    if (force && !window.confirm('Sei sicuro di voler cancellare tutti i prodotti locali e riscaricarli?')) {
      return;
    }

    setIsSyncing(true);
    setToast({ open: true, message: force ? 'Download completo in corso...' : 'Sincronizzazione in corso...', severity: 'info' });
    
    const result = await syncProducts(force);
    
    setToast({ 
      open: true, 
      message: result.message, 
      severity: result.success ? 'success' : 'error' 
    });
    setIsSyncing(false);
  };

  const handleSyncAttributes = async () => {
    setIsSyncing(true);
    setToast({ open: true, message: 'Sincronizzazione attributi in corso...', severity: 'info' });
    
    const result = await syncAttributes();
    
    setToast({ 
      open: true, 
      message: result.message, 
      severity: result.success ? 'success' : 'error' 
    });
    setIsSyncing(false);
  };

  const handleSyncProductAttributes = async () => {
    if (!window.confirm('Sei sicuro di voler cancellare tutte le associazioni attributi-prodotto locali e riscaricarle?')) {
      return;
    }

    setIsSyncing(true);
    setToast({ open: true, message: 'Sincronizzazione associazioni attributi in corso...', severity: 'info' });
    
    const result = await syncProductAttributes();
    
    setToast({ 
      open: true, 
      message: result.message, 
      severity: result.success ? 'success' : 'error' 
    });
    setIsSyncing(false);
  };

  const handleSyncCategories = async () => {
    const count = await db.techyshop_categorie.count();
    if (count > 0) {
      if (!window.confirm(`Sono presenti ${count} categorie locali. Vuoi resettare la tabella e riscaricarle dal server?`)) {
        return;
      }
    }

    setIsSyncing(true);
    setToast({ open: true, message: 'Sincronizzazione categorie in corso...', severity: 'info' });
    
    const result = await syncCategories(true);
    
    setToast({ 
      open: true, 
      message: result.message, 
      severity: result.success ? 'success' : 'error' 
    });
    setIsSyncing(false);
  };

  const handleResetTelegramUsers = async () => {
    if (!window.confirm('Sei sicuro di voler cancellare tutti gli utenti Telegram locali e riscaricarli dal server?')) {
      return;
    }

    setIsResettingTelegram(true);
    setToast({ open: true, message: 'Reset utenti Telegram in corso...', severity: 'info' });

    try {
      // 1. Cancella utenti locali
      await db.users.clear();
      log("Tabella utenti Telegram svuotata.", 'INFO');

      // 2. Scarica nuovi utenti
      const response = await fetch('https://deadapi.techysumo.it/telegram/listUsers', {
        headers: {
          'Authorization': process.env.REACT_APP_API_KEY,
        }
      });
      
      const result = await response.json();

      if (result.result && Array.isArray(result.details)) {
        // 3. Salva in Dexie
        const usersToSave = result.details.map(u => ({
          ...u,
          // Ensure requested fields are present
          username: u.username || u.first_name || u.id?.toString() || 'Utente',
          date: u.date || '',
          is_bot: u.is_bot || false,
          auth: u.auth || false,
          // Maintain compatibility with existing fields if needed
          status: u.status || (u.auth ? 'Active' : 'Inactive'),
          role: u.role || 'User',
          lastLogin: u.lastLogin || u.date || 'Mai'
        }));

        await db.users.bulkAdd(usersToSave);
        
        // Aggiorna timestamp ultimo aggiornamento
        await db.genericTable.put({ 
          table: 'telegram', 
          field: 'lastUpdateTelegramUsers', 
          value: new Date().toISOString() 
        });

        log(`Sincronizzati ${usersToSave.length} utenti Telegram.`, 'INFO');
        setToast({ open: true, message: `Reset completato! ${usersToSave.length} utenti scaricati.`, severity: 'success' });
      } else {
        throw new Error(result.text || "Errore durante il download degli utenti.");
      }
    } catch (error) {
      log("Errore nel reset utenti Telegram:", 'ERROR', error);
      setToast({ open: true, message: `Errore: ${error.message}`, severity: 'error' });
    } finally {
      setIsResettingTelegram(false);
    }
  };

  const handleResetTelegramTranslations = async () => {
    if (!window.confirm('Sei sicuro di voler cancellare tutte le traduzioni locali e riscaricarle dal server?')) {
      return;
    }

    setIsResettingTranslations(true);
    setToast({ open: true, message: 'Reset traduzioni in corso...', severity: 'info' });

    try {
      // 1. Cancella traduzioni locali
      await db.strings.clear();
      log("Tabella traduzioni svuotata.", 'INFO');

      // 2. Scarica nuove traduzioni (chiamata senza parametri per scaricare tutto)
      const result = await getTelegramTranslations();

      if (result) {
        log(`Sincronizzate ${result.length} traduzioni Telegram.`, 'INFO');
        setToast({ open: true, message: `Reset completato! ${result.length} traduzioni scaricate.`, severity: 'success' });
      } else {
        throw new Error("Errore durante il download delle traduzioni.");
      }
    } catch (error) {
      log("Errore nel reset traduzioni Telegram:", 'ERROR', error);
      setToast({ open: true, message: `Errore: ${error.message}`, severity: 'error' });
    } finally {
      setIsResettingTranslations(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/')} aria-label="back" sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          Impostazioni
        </Typography>
      </Box>

      <Paper sx={{ width: '100%', mb: 4, backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          centered
          sx={{ borderBottom: 1, borderColor: 'rgba(255,255,255,0.1)' }}
        >
          <Tab label="Strumenti & Utility" />
          <Tab label="Configurazione Generale" />
          <Tab label="TechyShop" />
          <Tab label="Telegram Bot" />
        </Tabs>

        {/* Tab 0: Strumenti */}
        {activeTab === 0 && (
          <Box sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>Utility di Sistema</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ListAlt />}
                  onClick={() => setIsLogModalOpen(true)}
                  sx={{ height: '80px', borderRadius: 2 }}
                >
                  Visualizza Log
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteSweep />}
                  onClick={clearLogs}
                  sx={{ height: '80px', borderRadius: 2 }}
                >
                  Svuota Log
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Tab 1: Configurazione Generale */}
        {activeTab === 1 && (
          <Box sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>Parametri Generali</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: '600px' }}>
              <TextField
                fullWidth
                label="Nome del Sito"
                variant="outlined"
                value={settings.siteName}
                onChange={(e) => setSettings({ ...settings, siteName: e.target.value })}
              />
              <TextField
                fullWidth
                label="Endpoint API"
                variant="outlined"
                value={settings.apiEndpoint}
                onChange={(e) => setSettings({ ...settings, apiEndpoint: e.target.value })}
              />
              <Button
                variant="contained"
                color="primary"
                startIcon={<Save />}
                onClick={handleSave}
                sx={{ alignSelf: 'flex-start', mt: 1 }}
              >
                Salva Modifiche
              </Button>
            </Box>
          </Box>
        )}

        {/* Tab 2: TechyShop */}
        {activeTab === 2 && (
          <Box sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>Gestione Catalogo TechyShop</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 3, height: '100%', backgroundColor: 'transparent' }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    Sincronizzazione Incrementale
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3, opacity: 0.7 }}>
                    Scarica solo i nuovi prodotti o quelli aggiornati dall'ultima sincronizzazione effettuata.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Sync />}
                    disabled={isSyncing}
                    onClick={() => handleSync(false)}
                    fullWidth
                  >
                    Sincronizza Nuovi Prodotti
                  </Button>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 3, height: '100%', backgroundColor: 'transparent' }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    Aggiornamento Forzato
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3, opacity: 0.7 }}>
                    Cancella tutti i prodotti locali e riscarica l'intero catalogo. Da usare in caso di incongruenze.
                  </Typography>
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<CloudDownload />}
                    disabled={isSyncing}
                    onClick={() => handleSync(true)}
                    fullWidth
                  >
                    Svuota e Riscarica Tutto
                  </Button>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 3, height: '100%', backgroundColor: 'transparent' }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    Sincronizzazione Attributi
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3, opacity: 0.7 }}>
                    Aggiorna l'elenco degli attributi e dei relativi valori predefiniti dal server.
                  </Typography>
                  <Button
                    variant="outlined"
                    color="info"
                    startIcon={<Assignment />}
                    disabled={isSyncing}
                    onClick={handleSyncAttributes}
                    fullWidth
                  >
                    Sincronizza Attributi
                  </Button>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 3, height: '100%', backgroundColor: 'transparent' }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    Sincronizzazione Categorie
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3, opacity: 0.7 }}>
                    Aggiorna l'elenco delle categorie e la loro gerarchia dal server.
                  </Typography>
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<Category />}
                    disabled={isSyncing}
                    onClick={handleSyncCategories}
                    fullWidth
                  >
                    Sincronizza Categorie
                  </Button>
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 3, height: '100%', backgroundColor: 'transparent' }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                    Associazioni Attributi
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3, opacity: 0.7 }}>
                    Reset e download completo di tutte le associazioni tra prodotti ed attributi.
                  </Typography>
                  <Button
                    variant="outlined"
                    color="success"
                    startIcon={<Assignment />}
                    disabled={isSyncing}
                    onClick={handleSyncProductAttributes}
                    fullWidth
                  >
                    Sync Associazioni Prodotti
                  </Button>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Tab 3: Telegram Bot */}
        {activeTab === 3 && (
          <Box sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>Gestione Telegram Bot</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 3, height: '100%', backgroundColor: 'transparent' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SmartToy sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Anagrafica Utenti
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ mb: 3, opacity: 0.7 }}>
                    Cancella tutti gli utenti salvati localmente e riscarica l'elenco completo dal bot Telegram.
                  </Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={isResettingTelegram ? <CircularProgress size={20} /> : <RestartAlt />}
                    disabled={isResettingTelegram}
                    onClick={handleResetTelegramUsers}
                    fullWidth
                  >
                    Reset Utenti
                  </Button>
                </Paper>
              </Grid>

              <Grid item xs={12} md={4}>
                <Paper variant="outlined" sx={{ p: 3, height: '100%', backgroundColor: 'transparent' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Translate sx={{ mr: 1, color: 'secondary.main' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Traduzioni Stringhe
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ mb: 3, opacity: 0.7 }}>
                    Svuota la cache locale delle traduzioni e forza il download di tutte le stringhe dal server.
                  </Typography>
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={isResettingTranslations ? <CircularProgress size={20} /> : <RestartAlt />}
                    disabled={isResettingTranslations}
                    onClick={handleResetTelegramTranslations}
                    fullWidth
                  >
                    Reset Traduzioni
                  </Button>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>


      {/* Modal Log Fullscreen */}
      <Dialog 
        fullScreen
        open={isLogModalOpen} 
        onClose={() => setIsLogModalOpen(false)}
        TransitionComponent={Transition}
      >
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => setIsLogModalOpen(false)}
              aria-label="close"
            >
              <Close />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              Log di Sistema
            </Typography>
            <Button 
              color="inherit" 
              startIcon={<DeleteSweep />} 
              onClick={clearLogs}
            >
              Svuota Log
            </Button>
          </Toolbar>
        </AppBar>
        <DialogContent dividers sx={{ backgroundColor: 'background.default' }}>
          {!logs ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', py: 10 }}>
              <Loader />
              <Typography sx={{ mt: 2 }}>Caricamento log...</Typography>
            </Box>
          ) : (
            <List sx={{ width: '100%', maxWidth: '1200px', mx: 'auto' }}>
              {logs.length === 0 ? (
                <Typography align="center" sx={{ py: 10, opacity: 0.5 }}>Nessun log disponibile.</Typography>
              ) : (
                logs.map((log) => (
                  <ListItem 
                    key={log.id} 
                    divider 
                    sx={{ 
                      flexDirection: 'column', 
                      alignItems: 'flex-start',
                      py: 2,
                      '&:last-child': { borderBottom: 'none' }
                    }}
                  >
                    <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', mb: 1 }}>
                      <Chip 
                        label={log.type} 
                        size="small" 
                        color={log.type === 'ERROR' ? 'error' : 'primary'} 
                        variant="outlined"
                      />
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {new Date(log.timestamp).toLocaleString()}
                      </Typography>
                    </Box>
                    <ListItemText 
                      primary={log.message} 
                      secondary={typeof log.details === 'string' ? log.details : JSON.stringify(log.details, null, 2)}
                      primaryTypographyProps={{ variant: 'h6', sx: { fontSize: '1.1rem', mb: 0.5 } }}
                      secondaryTypographyProps={{ variant: 'body2', sx: { whiteSpace: 'pre-wrap', fontFamily: 'monospace' } }}
                    />
                  </ListItem>
                ))
              )}
            </List>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default Settings;
